import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import OrderCustomSelect from "./OrderCustomSelect";
import JsonSchemaForm from "../common/JsonSchemaFormSelector";
import { getGroups } from "../../common/api/groups";
import { getUsersByClient } from "../../common/api/users";
import { getOrderSchemas, updateOrder ,reassignOrder  } from "../../common/api/orders";
import { useToast } from "../../hooks/useToast";
import "./ModalEditAssignment.css";

const EMPTY_VALUE = "";

const resolveClientCodeFromOrder = (order) => {
  if (!order || typeof order !== "object") {
    return "";
  }

  const candidates = [
    order.COD_CLIENTE,
    order.COD_ID_CLIENTE,
    order.COD_CLIENTE_PARTE,
    order.COD_ID_GROUP,
    order.COD_ID_GRUPO,
    order.codCliente,
    order.cod_cliente,
    order.cod_id_group,
    order.CLIENTE,
    order.ID_CLIENTE,
  ];

  for (const candidate of candidates) {
    if (
      candidate !== undefined &&
      candidate !== null &&
      `${candidate}`.trim() !== ""
    ) {
      return `${candidate}`;
    }
  }

  return "";
};

const mapGroupOption = (group) => {
  if (!group || typeof group !== "object") {
    return null;
  }

  const value =
    group.COD_ID || group.cod_id || group.ID || group.id || group.COD_ID_GRUPO;

  if (value === undefined || value === null) {
    return null;
  }

  const label =
    group.NOMBRE || group.nombre || group.GRUPO || group.NAME || `ID ${value}`;

  return {
    value: `${value}`,
    label: `${label}`,
  };
};

const resolveOrderId = (order) => {
  if (!order || typeof order !== "object") {
    return "";
  }

  const candidates = [
    order.COD_ID,
    order.COD_ID_PARTE,
    order.CODIGO,
    order.ID,
    order.id,
    order.cod_id,
  ];

  for (const candidate of candidates) {
    if (
      candidate !== undefined &&
      candidate !== null &&
      `${candidate}`.trim() !== ""
    ) {
      return `${candidate}`;
    }
  }

  return "";
};

const mapUserOption = (user) => {
  if (!user || typeof user !== "object") {
    return null;
  }

  const value = user.COD_ID || user.cod_id || user.ID || user.id;
  if (value === undefined || value === null) {
    return null;
  }

  const label =
    user.USER ||
    user.user ||
    user.NOMBRE ||
    user.NOMBRE_COMPLETO ||
    user.EMAIL ||
    `ID ${value}`;

  return {
    value: `${value}`,
    label: `${label}`,
  };
};

function normalizeSchemasResponse(rawSchemas) {
  if (!rawSchemas) return [];

  // Se è un singolo JSON Schema con allOf (come quello dell'API)
  if (
    rawSchemas.$schema &&
    rawSchemas.properties &&
    Array.isArray(rawSchemas.allOf)
  ) {
    const paramsProperties =
      rawSchemas.properties?.Params?.properties || rawSchemas.properties || {};
    const paramsOrder =
      rawSchemas.properties?.Params?.order ||
      rawSchemas.properties?.Params?.paramsOrder ||
      rawSchemas.properties?.Params?.params_order ||
      rawSchemas.properties?.Params?.paramOrder ||
      Object.keys(paramsProperties);

    const tipos = rawSchemas.allOf
      .map((block) => {
        const tipoValue = block.if?.properties?.TipoParte?.const;
        const descripcion = block.then?.properties?.TipoParteDescripcion?.const;
        if (tipoValue !== undefined) {
          return {
            id: String(tipoValue),
            label: descripcion || `Tipo ${tipoValue}`,
            schema: rawSchemas, // lo schema principale
            paramsOrder,
          };
        }
        return null;
      })
      .filter(Boolean);
    return tipos;
  }

  // Fallback per vecchi formati
  const schemaItems = Array.isArray(rawSchemas)
    ? rawSchemas
    : rawSchemas.schemas || rawSchemas.Schemas || rawSchemas.data || [];
  return schemaItems.map((item) => ({
    id: String(item.id || item.TipoParte || ""),
    label: item.label || item.description || `Tipo ${item.id}`,
    schema: item.schema || item,
    paramsOrder: Object.keys(item.schema?.properties || {}),
  }));
}
// 🔧 COPIATA dall'originale ModalEditAssignment
const computeSchemaDefaults = (schema) => {
  if (!schema?.properties) {
    return {};
  }

  return Object.entries(schema.properties).reduce((acc, [key, property]) => {
    if (property?.default !== undefined) {
      acc[key] = property.default;
    }
    return acc;
  }, {});
};

const resolveOrderTypeId = (order) => {
  if (!order || typeof order !== "object") {
    return "";
  }

  const candidates = [
    order.TipoParte,
    order.tipoParte,
    order.tipo_parte,
    order.TIPO_PARTE,
    order.TIPOPARTE,
    order.Tipo,
    order.tipo,
    order.type,
  ];

  for (const candidate of candidates) {
    if (
      candidate !== undefined &&
      candidate !== null &&
      `${candidate}`.trim() !== ""
    ) {
      return `${candidate}`;
    }
  }

  return "";
};

// 🔧 COPIATA dall'originale ModalEditAssignment
const normalizeRawParamsValue = (value) => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "object") {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) || (parsed && typeof parsed === "object")) {
          return parsed;
        }
      } catch (error) {
        console.warn("No se pudo parsear PARAMS como JSON", error);
      }
    }

    if (trimmed.includes("|")) {
      return trimmed.split("|");
    }

    if (trimmed.includes(";")) {
      return trimmed.split(";");
    }

    if (trimmed.includes(",")) {
      return trimmed.split(",");
    }

    return [trimmed];
  }

  return [];
};

// 🔧 COPIATA dall'originale ModalEditAssignment
const resolveOrderParams = (order) => {
  if (!order || typeof order !== "object") {
    return [];
  }

  const candidates = [
    order.PARAMS,
    order.Params,
    order.params,
    order.Param,
    order.param,
    order.Parametros,
    order.parametros,
    order.parameters,
    order.PARAMETERS,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeRawParamsValue(candidate);
    if (Array.isArray(normalized) && normalized.length > 0) {
      return normalized;
    }

    if (normalized && typeof normalized === "object") {
      return normalized;
    }
  }

  return [];
};

// 🔧 COPIATA dall'originale ModalEditAssignment
const parseParamValue = (value, propertySchema = {}) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  if (propertySchema.type === "boolean") {
    if (typeof value === "boolean") {
      return value;
    }

    const normalized = String(value).trim().toLowerCase();
    return ["true", "1", "si", "sí", "yes", "y"].includes(normalized);
  }

  if (propertySchema.type === "number" || propertySchema.type === "integer") {
    const numeric = Number(value);
    return Number.isNaN(numeric) ? undefined : numeric;
  }

  if (propertySchema.type === "array") {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (_error) {
        // Ignorar y probar con split manual
      }

      return trimmed
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
    }
  }

  return value;
};

// 🔧 COPIATA dall'originale ModalEditAssignment
const formatParamValue = (value, propertySchema) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (propertySchema?.type === "boolean") {
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      return normalized === "true" ||
        normalized === "si" ||
        normalized === "sí" ||
        normalized === "1"
        ? "SI"
        : "NO";
    }
    return value ? "SI" : "NO";
  }

  if (propertySchema?.type === "number" || propertySchema?.type === "integer") {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return "";
    }
    return numeric.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(",");
  }

  return String(value);
};

// 🔧 COPIATA dall'originale ModalEditAssignment
const buildFormDataFromParams = (paramsSource, schemaItem) => {
  if (!schemaItem?.schema?.properties) {
    return {};
  }

  // Blocco DIFF2 (che già hai)
  let effectiveProperties = schemaItem.schema.properties;
  
  if (effectiveProperties.Params?.properties) {
    //console.log("🔍 [DIFF2] Found nested Params.properties!");
    //console.log("🔍 [DIFF2] Nested fields:", Object.keys(effectiveProperties.Params.properties));
    effectiveProperties = effectiveProperties.Params.properties;
  } else {
    console.log("🔍 [DIFF2] Using direct properties");
  }

  // ⭐ AGGIUNGERE QUESTI LOG QUI ⭐
  //console.log("🔍 [DIFF2] paramsSource:", paramsSource);
//console.log("🔍 [DIFF2] paramsSource type:", Array.isArray(paramsSource) ? 'array' : typeof paramsSource);
  console.log("🔍 [DIFF2] paramsSource length/keys:", 
    Array.isArray(paramsSource) 
      ? paramsSource.length 
      : (typeof paramsSource === 'object' ? Object.keys(paramsSource) : 'N/A')
  );
  //console.log("🔍 [DIFF2] schemaItem.paramsOrder:", schemaItem.paramsOrder);
  // ⭐ FINE LOG ⭐

 const propertyKeys = Object.keys(effectiveProperties);
  
  //console.log("🔍 [DIFF2] propertyKeys to process:", propertyKeys);
  //console.log("🔍 [DIFF2] Will map", paramsSource.length, "values to", propertyKeys.length, "fields");

  //console.log("🔍 [DIFF2] propertyKeys to process:", propertyKeys);

  return propertyKeys.reduce((acc, key, index) => {
    const propertySchema = effectiveProperties[key] || {};
    let rawValue;

    if (Array.isArray(paramsSource)) {
      rawValue = paramsSource[index];
      //console.log(`🔍 [DIFF2] Key "${key}" [${index}]: rawValue from array =`, rawValue);
    } else if (paramsSource && typeof paramsSource === "object") {
      rawValue = paramsSource[key];
      //console.log(`🔍 [DIFF2] Key "${key}": rawValue from object =`, rawValue);
    }

    const parsed = parseParamValue(rawValue, propertySchema);
    //console.log(`🔍 [DIFF2] Key "${key}": parsed =`, parsed);
    
    if (parsed !== undefined) {
      acc[key] = parsed;
    }

    return acc;
  }, {});
};

// ⭐ FUNZIONE CORRETTA - buildParamsFromFormData ⭐
// Versione che usa le chiavi di effectiveProperties (SCHEMA) invece di paramsSource (DATI)

const buildParamsFromFormData = (schemaItem, formData = {}) => {
  if (!schemaItem?.schema?.properties) {
    return [];
  }

  console.log("🔍 [buildParams] INPUT schemaItem.id:", schemaItem.id);
  console.log("🔍 [buildParams] INPUT formData:", formData);

  let effectiveProperties = schemaItem.schema.properties;
  let paramsSource = formData;
  let paramsOrderOverride = schemaItem.paramsOrder;

  // ⭐ CONTROLLA SE I DATI SONO NESTED IN formData.Params ⭐
  if (formData.Params && typeof formData.Params === 'object') {
    console.log("✅ [buildParams] Found nested formData.Params");
    paramsSource = formData.Params;
    
    // Se lo schema ha anche Params.properties, usa quelli
    if (effectiveProperties.Params?.properties) {
      console.log("✅ [buildParams] Found schema.properties.Params.properties");
      effectiveProperties = effectiveProperties.Params.properties;
      
      paramsOrderOverride =
        schemaItem.schema.properties.Params.order ||
        schemaItem.schema.properties.Params.paramsOrder ||
        schemaItem.schema.properties.Params.params_order ||
        schemaItem.schema.properties.Params.paramOrder ||
        schemaItem.paramsOrder;
    } else if (schemaItem.schema.allOf && Array.isArray(schemaItem.schema.allOf)) {
      // ⭐ GESTISCI IL BLOCCO allOf CONDIZIONALE ⭐
      console.log("✅ [buildParams] Found schema.allOf, resolving conditional block");
      
      // ⭐ USA schemaItem.id COME TipoParte ⭐
      const tipoParteValue = Number(schemaItem.id);
      console.log("🔍 [buildParams] TipoParte value from schemaItem.id:", tipoParteValue);
      
      // Trova il blocco condizionale che corrisponde a questo TipoParte
      const matchingBlock = schemaItem.schema.allOf.find(
        (block) => block.if?.properties?.TipoParte?.const === tipoParteValue
      );
      
      if (matchingBlock?.then?.properties) {
        console.log("✅ [buildParams] Found matching conditional block!");
        console.log("🔍 [buildParams] Conditional then.properties keys:", Object.keys(matchingBlock.then.properties));
        
        // ⭐ CONTROLLA SE IL BLOCCO HA Params.properties NESTED ⭐
        if (matchingBlock.then.properties.Params?.properties) {
          console.log("✅ [buildParams] Found nested Params.properties in conditional block!");
          console.log("🔍 [buildParams] Nested Params fields:", Object.keys(matchingBlock.then.properties.Params.properties));
          
          // Usa le properties da dentro Params
          effectiveProperties = matchingBlock.then.properties.Params.properties;
          
          // Cerca l'order dentro Params
          paramsOrderOverride = 
            matchingBlock.then.properties.Params.order ||
            matchingBlock.then.properties.Params.paramsOrder ||
            matchingBlock.then.properties.Params.params_order ||
            matchingBlock.then.properties.Params.paramOrder ||
            null; // ⭐ NON usare schemaItem.paramsOrder che ha TipoParte/TipoParteDescripcion
        } else {
          // Usa le properties dal blocco then direttamente, escludendo i metadati
          effectiveProperties = Object.keys(matchingBlock.then.properties).reduce((acc, key) => {
            if (key !== 'TipoParte' && key !== 'TipoParteDescripcion') {
              acc[key] = matchingBlock.then.properties[key];
            }
            return acc;
          }, {});
          
          paramsOrderOverride = 
            matchingBlock.then.order ||
            matchingBlock.then.paramsOrder ||
            matchingBlock.then.params_order ||
            matchingBlock.then.paramOrder ||
            null;
        }
        
        console.log("🔍 [buildParams] effectiveProperties keys after allOf:", Object.keys(effectiveProperties));
      } else {
        console.warn("⚠️ [buildParams] No matching conditional block found for TipoParte:", tipoParteValue);
        console.warn("⚠️ [buildParams] Available TipoParte values in allOf:", 
          schemaItem.schema.allOf.map(b => b.if?.properties?.TipoParte?.const).filter(Boolean)
        );
        
        // ⭐ FALLBACK: NON usare paramsSource, usa lo schema base ⭐
        // Questo dovrebbe essere l'ultimo resort
        effectiveProperties = {};
        console.error("❌ [buildParams] Cannot resolve schema, PARAMS will be empty!");
      }
      
      // Reset per usare l'ordine delle chiavi se non c'è un order specifico
      if (!Array.isArray(paramsOrderOverride) || paramsOrderOverride.length === 0) {
        paramsOrderOverride = null;
      }
    } else {
      console.log("⚠️ [buildParams] No schema definition found, cannot build PARAMS");
      effectiveProperties = {};
    }
  } else if (effectiveProperties.Params?.properties) {
    // Caso legacy: Params solo nello schema
    console.log("✅ [buildParams] Using schema.properties.Params.properties (legacy)");
    effectiveProperties = effectiveProperties.Params.properties;
    paramsSource = formData.Params || {};
    
    paramsOrderOverride =
      schemaItem.schema.properties.Params.order ||
      schemaItem.schema.properties.Params.paramsOrder ||
      schemaItem.schema.properties.Params.params_order ||
      schemaItem.schema.properties.Params.paramOrder ||
      schemaItem.paramsOrder;
  }

  const resolvedParamsOrder = Array.isArray(paramsOrderOverride)
    ? paramsOrderOverride
    : [];

  // ⭐ USA SEMPRE Object.keys(effectiveProperties) - le chiavi dello SCHEMA ⭐
  // NON usare Object.keys(paramsSource) perché ha solo i campi compilati
  const propertyKeys = resolvedParamsOrder.length > 0
    ? resolvedParamsOrder
    : Object.keys(effectiveProperties);

  // ⭐ FILTRO: escludi sempre TipoParte e TipoParteDescripcion ⭐
  const filteredKeys = propertyKeys.filter(
    key => key !== 'TipoParte' && key !== 'TipoParteDescripcion'
  );

  console.log("🔍 [buildParams] effectiveProperties keys:", Object.keys(effectiveProperties));
  console.log("🔍 [buildParams] paramsSource keys:", Object.keys(paramsSource));
  console.log("🔍 [buildParams] resolvedParamsOrder:", resolvedParamsOrder);
  console.log("🔍 [buildParams] propertyKeys:", propertyKeys);
  console.log("🔍 [buildParams] filteredKeys:", filteredKeys);

  // ⭐ MAPPA OGNI CAMPO DELLO SCHEMA, anche se non presente in paramsSource ⭐
  const result = filteredKeys.map((key) => {
    const propertySchema = effectiveProperties[key] || {};
    const value = paramsSource[key]; // Può essere undefined per campi non compilati
    
    // ⭐ SE IL VALORE È undefined, null, o stringa vuota → usa "" ⭐
    if (value === undefined || value === null || value === "") {
      console.log(`🔍 [buildParams] ${key}: EMPTY → ""`);
      return "";
    }
    
    const formatted = formatParamValue(value, propertySchema);
    console.log(`🔍 [buildParams] ${key}: ${JSON.stringify(value)} → "${formatted}"`);
    
    // ⭐ SE formatParamValue ritorna undefined/null/empty, usa "" ⭐
    return formatted === undefined || formatted === null || formatted === "" 
      ? "" 
      : formatted;
  });

  console.log("🔍 [buildParams] Final PARAMS array:", result);
  console.log("🔍 [buildParams] Final PARAMS length:", result.length);
  return result;
};
function ModalEditAssignment({ order, clientCode, onClose, onSave }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [schemas, setSchemas] = useState([]);
  const [formData, setFormData] = useState({});
  // Add after existing useState declarations
  const [direccionSuministro, setDireccionSuministro] = useState("");
  const [coordenadaX, setCoordenadaX] = useState("");
  const [coordenadaY, setCoordenadaY] = useState("");
  const [nombreCT, setNombreCT] = useState("");
  const [tipoInstalacion, setTipoInstalacion] = useState("");
  const [ruta, setRuta] = useState("");
  const [tipoEquipo, setTipoEquipo] = useState("");
  const [selectedSchemaId, setSelectedSchemaId] = useState(EMPTY_VALUE);

  const selectedSchema = useMemo(
    () => schemas.find((schemaItem) => schemaItem.id === selectedSchemaId) || null,
    [schemas, selectedSchemaId]
  );

  const effectiveClientCode = useMemo(() => {
    if (clientCode && `${clientCode}`.trim() !== "") {
      return `${clientCode}`;
    }
    return resolveClientCodeFromOrder(order);
  }, [clientCode, order]);

  // Impostiamo il tipo parte iniziale dall'order
  useEffect(() => {
    const initialTipoParte = resolveOrderTypeId(order);
    setSelectedSchemaId(initialTipoParte);
     if (order) {
      setDireccionSuministro(order.DIRECCIONSUMINISTRO || "");
      setCoordenadaX(order.COORDENADA_X || "");
      setCoordenadaY(order.COORDENADA_Y || "");
      setNombreCT(order.NOMBRECT || "");
      setTipoInstalacion(order.TIPO_INSTALACION || "");
      setRuta(order.RUTA || "");
      setTipoEquipo(order.Te || order.TE || "");
    }
  }, [order]);

  // Inizializza gruppo e usuario selezionati
  useEffect(() => {
    const initialGroup =
      order?.COD_ID_USER_GROUP !== undefined &&
      order?.COD_ID_USER_GROUP !== null
        ? `${order.COD_ID_USER_GROUP}`
        : "";
    const initialUser =
      order?.COD_ID_USER !== undefined && order?.COD_ID_USER !== null
        ? `${order.COD_ID_USER}`
        : "";

    setSelectedGroup(initialGroup);
    setSelectedUser(initialGroup ? "" : initialUser);
  }, [order]);

  // Carica dati iniziali (gruppi, utenti, schemas)
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [groupsResponse, usersResponse, schemasResponse] =
          await Promise.all([
            getGroups().catch((error) => {
              console.warn("No se pudieron cargar los grupos", error);
              return [];
            }),
            effectiveClientCode
              ? getUsersByClient(effectiveClientCode).catch((error) => {
                  console.warn(
                    "No se pudieron cargar usuarios del cliente",
                    error
                  );
                  return [];
                })
              : Promise.resolve([]),
            getOrderSchemas().catch((error) => {
              console.error(
                "No se pudieron cargar los esquemas de partes",
                error
              );
              toast.error("No se pudo cargar el formulario del parte");
              return [];
            }),
          ]);

        if (!isMounted) {
          return;
        }

        const filteredGroups = Array.isArray(groupsResponse)
          ? groupsResponse.filter((group) => {
              const groupClient =
                group?.COD_CLIENTE ||
                group?.cod_cliente ||
                group?.CLIENTE ||
                group?.ID_CLIENTE;

              if (!effectiveClientCode) {
                return true;
              }

              if (groupClient === undefined || groupClient === null) {
                return false;
              }

              return `${groupClient}` === `${effectiveClientCode}`;
            })
          : [];

        const normalizedGroups = filteredGroups
          .map(mapGroupOption)
          .filter(Boolean);

        const normalizedUsers = Array.isArray(usersResponse)
          ? usersResponse.map(mapUserOption).filter(Boolean)
          : [];

        const normalizedSchemas = normalizeSchemasResponse(schemasResponse);
        
        setGroupOptions(normalizedGroups);
        setUserOptions(normalizedUsers);
        setSchemas(normalizedSchemas);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        toast.error(
          "No se pudieron cargar los datos necesarios para editar el parte"
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [effectiveClientCode, toast]);

  // 🔧 POPOLA IL FORM - COPIATO dall'originale ModalEditAssignment
  useEffect(() => {
  if (!selectedSchema) {
    setFormData({});
    return;
  }

  //console.log("🔍 [DIFF1] Starting schema resolution");
  //console.log("🔍 [DIFF1] selectedSchemaId:", selectedSchemaId);
  //console.log("🔍 [DIFF1] selectedSchema.schema has allOf?", !!selectedSchema.schema.allOf);
  
  let effectiveSchema = selectedSchema.schema;
  
  if (selectedSchema.schema.allOf && Array.isArray(selectedSchema.schema.allOf)) {
    //console.log("✅ [DIFF1] Schema HAS allOf, processing...");
    //console.log("🔍 [DIFF1] allOf blocks count:", selectedSchema.schema.allOf.length);
    
    const tipoParteNumeric = Number(selectedSchemaId);
    //console.log("🔍 [DIFF1] Looking for TipoParte:", tipoParteNumeric);
    
    const matchingBlock = selectedSchema.schema.allOf.find(
      (block) => block.if?.properties?.TipoParte?.const === tipoParteNumeric
    );
    
    if (matchingBlock?.then?.properties) {
      //console.log("✅ [DIFF1] Found matching block!");
      //console.log("🔍 [DIFF1] Base properties:", Object.keys(selectedSchema.schema.properties));
      //console.log("🔍 [DIFF1] Conditional properties:", Object.keys(matchingBlock.then.properties));
      
      effectiveSchema = {
        ...selectedSchema.schema,
        properties: {
          ...selectedSchema.schema.properties,
          ...matchingBlock.then.properties,
        },
      };
      
      //console.log("✅ [DIFF1] Merged properties:", Object.keys(effectiveSchema.properties));
    } else {
      console.log("❌ [DIFF1] NO matching block found for TipoParte:", tipoParteNumeric);
    }
  } else {
    console.log("⚠️ [DIFF1] Schema does NOT have allOf, using schema as-is");
    //console.log("🔍 [DIFF1] Direct properties:", Object.keys(selectedSchema.schema.properties || {}));
  }

  const defaults = computeSchemaDefaults(effectiveSchema);
  const paramsSource = resolveOrderParams(order);
  const valuesFromOrder = buildFormDataFromParams(
    paramsSource,
    { ...selectedSchema, schema: effectiveSchema }
  );

  //console.log("🔍 [DIFF1] Values from order:", valuesFromOrder);

  // ⭐ AGGIUNGERE QUESTA DICHIARAZIONE ⭐
  let finalFormData;

  if (effectiveSchema.properties.Params?.properties) {
    //console.log("🔍 [DIFF3] Wrapping values inside Params");
    finalFormData = {
      ...defaults,
      Params: valuesFromOrder
    };
  } else {
    //console.log("🔍 [DIFF3] Using flat values");
    finalFormData = {
      ...defaults,
      ...valuesFromOrder
    };
  }

  //console.log("🔍 [DIFF3] Final formData to set:", finalFormData);

  setFormData(finalFormData);
  }, [order, selectedSchema, selectedSchemaId]);

 const handleSaveUserOrGroup = async () => {
  if (saving) {
    return;
  }

  if (!selectedGroup && !selectedUser) {
    toast.error("Selecciona al menos un grupo o un usuario");
    return;
  }

  const targetOrderId = resolveOrderId(order);
  if (!targetOrderId) {
    toast.error("El parte seleccionado no tiene un identificador válido");
    return;
  }

  if (!effectiveClientCode) {
    toast.error("No se pudo determinar el cliente del parte");
    return;
  }

  console.log("🔍 [handleSave] Reassigning order:", {
    orderId: targetOrderId,
    clientCode: effectiveClientCode,
    group: selectedGroup || null,
    user: selectedUser || null
  });

  try {
    setSaving(true);
    
    // ⭐ USA reassignOrder INVECE DI updateOrders ⭐
    const assignment = selectedGroup 
      ? { COD_ID_USER_GROUP: selectedGroup }
      : { COD_ID_USER: selectedUser };
    
    await reassignOrder(targetOrderId, effectiveClientCode, assignment);

    toast.success("Asignación actualizada correctamente");

    const callbackResult = onSave
      ? await onSave({
          orderId: targetOrderId,
          groupId: selectedGroup || null,
          userId: selectedUser || null,
          clientCode: effectiveClientCode || null,
        })
      : true;

    if (callbackResult !== false) {
      onClose();
    }
  } catch (err) {
    console.error("❌ Error al reasignar:", err);
    toast.error("Error al cambiar la asignación: " + err);
  } finally {
    setSaving(false);
  }
};
  
const handleSave = async () => {
  if (saving) {
      return;
    }

    const targetOrderId = resolveOrderId(order);
    if (!targetOrderId) {
      toast.error("El parte seleccionado no tiene un identificador válido");
      return;
    }

    if (!effectiveClientCode) {
      toast.error("No se pudo determinar el cliente del parte");
      return;
    }

    const payload = {
      COD_ID: targetOrderId,
      COD_ID_GROUP: effectiveClientCode,
    };

    if (selectedGroup) {
      payload.COD_ID_USER_GROUP = selectedGroup;
    } else if (selectedUser) {
      payload.COD_ID_USER = selectedUser;
    }

    if (direccionSuministro.trim()) {
      payload.DIRECCIONSUMINISTRO = direccionSuministro.trim();
    }

    if (coordenadaX.trim()) {
      payload.COORDENADA_X = coordenadaX.trim();
    }

    if (coordenadaY.trim()) {
      payload.COORDENADA_Y = coordenadaY.trim();
    }

    if (nombreCT.trim()) {
      payload.NOMBRECT = nombreCT.trim();
    }

    if (tipoInstalacion.trim()) {
      payload.TIPO_INSTALACION = tipoInstalacion.trim();
    }

    if (ruta.trim()) {
      payload.RUTA = ruta.trim();
    }

    if (tipoEquipo.trim()) {
      payload.Te = tipoEquipo.trim();
    }

    if (selectedSchema) {
      const paramsArray = buildParamsFromFormData(selectedSchema, formData);
      if (Array.isArray(paramsArray) && paramsArray.length > 0) {
        payload.PARAMS = paramsArray;
      }
    }

    if (!payload.COD_ID_USER && !payload.COD_ID_USER_GROUP) {
      console.warn("Guardando sin cambios de asignación");
    }
//console.log(payload);
//return;
    try {
      setSaving(true);
      await updateOrder(payload);
      toast.success("Parte actualizado correctamente");

      const callbackResult = onSave
        ? await onSave({
            orderId: targetOrderId,
            groupId: selectedGroup || null,
            userId: selectedUser || null,
            clientCode: effectiveClientCode || null,
          })
        : true;

      if (callbackResult !== false) {
        onClose();
      }
    } catch (err) {
      console.error("❌ Error al actualizar el parte:", err);
      toast.error("Error al actualizar el parte: " + err);
    } finally {
      setSaving(false);
    }
  };
   return (
    <Modal title="Modificar Parte" onClose={onClose} position="top-left" className="modal-wide">
      {loading ? (
        <div
          className="input-group"
          style={{ textAlign: "center", padding: "2rem" }}
        >
          <p>Cargando datos…</p>
        </div>
      ) : (
        <div className="modal-edit-assignment">
          <div className="modal-edit-scroll">
            {/* 2. 🆕 CUPS and CntId - READ ONLY - First Row */}
            <div className="input-grid two-cols">
              <div className="input-group">
                <label>CUPS</label>
                <input
                  type="text"
                  value={order?.CUPS || ""}
                  disabled
                  className="input-readonly"
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
              <div className="input-group">
                <label>CntId</label>
                <input
                  type="text"
                  value={order?.CntId || ""}
                  disabled
                  className="input-readonly"
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>
            </div>

            {/* 🆕 Dirección de Suministro */}
            <div className="input-group">
              <label>Dirección de Suministro *</label>
              <input
                type="text"
                value={direccionSuministro}
                onChange={(e) => setDireccionSuministro(e.target.value)}
                placeholder="Ingrese la dirección de suministro"
              />
            </div>

            {/* 🆕 Ruta y Tipo de equipo */}
            <div className="input-grid two-cols">
              <div className="input-group">
                <label>Ruta</label>
                <input
                  type="text"
                  value={ruta}
                  onChange={(e) => setRuta(e.target.value)}
                  placeholder="Ruta asociada"
                />
              </div>
              <div className="input-group">
                <label>Tipo de equipo (Te)</label>
                <input
                  type="text"
                  value={tipoEquipo}
                  onChange={(e) => setTipoEquipo(e.target.value)}
                  placeholder="5, 4, contador, etc"
                />
              </div>
            </div>

            {/* 🆕 Coordenadas */}
            <div className="input-grid two-cols">
              <div className="input-group">
                <label>Coordenada X (Latitud WGS84) *</label>
                <input
                  type="text"
                  value={coordenadaX}
                  onChange={(e) => setCoordenadaX(e.target.value)}
                  placeholder="Ej: 40.416775"
                />
              </div>
              <div className="input-group">
                <label>Coordenada Y (Longitud WGS84) *</label>
                <input
                  type="text"
                  value={coordenadaY}
                  onChange={(e) => setCoordenadaY(e.target.value)}
                  placeholder="Ej: -3.703790"
                />
              </div>
            </div>

            {/* 🆕 Nombre CT y Tipo Instalación */}
            <div className="input-grid two-cols">
              <div className="input-group">
                <label>Nombre CT</label>
                <input
                  type="text"
                  value={nombreCT}
                  onChange={(e) => setNombreCT(e.target.value)}
                  placeholder="Nombre del centro de transformación"
                />
              </div>
              <div className="input-group">
                <label>Tipo Instalación *</label>
                <select
                  value={tipoInstalacion}
                  onChange={(e) => setTipoInstalacion(e.target.value)}
                >
                  <option value="">-- Seleccionar tipo --</option>
                  <option value="Monofasica">Monofásica</option>
                  <option value="Bifasica">Bifásica</option>
                  <option value="Trifasica">Trifásica</option>
                </select>
              </div>
            </div>

            {/* 2 & 3. Grupo asignado y Usuario asignado - Same row */}
            <div className="input-grid two-cols">
              <div className="input-group">
                <label>Grupo asignado</label>
                <OrderCustomSelect
                  options={groupOptions}
                  value={selectedGroup}
                  onChange={(value) => {
                    setSelectedGroup(value);
                    if (value) {
                      setSelectedUser("");
                    }
                  }}
                  placeholder="-- Seleccionar grupo --"
                />
              </div>

              <div className="input-group">
                <label>Usuario asignado</label>
                <OrderCustomSelect
                  options={userOptions}
                  value={selectedUser}
                  onChange={(value) => {
                    setSelectedUser(value);
                    if (value) {
                      setSelectedGroup("");
                    }
                  }}
                  placeholder="-- Seleccionar usuario --"
                />
              </div>
            </div>

            {/* 4. Tipo parte - READONLY */}
            <div className="input-group">
              <label>Tipo de parte</label>
              <input
                type="text"
                value={selectedSchema ? `${selectedSchema.id} - ${selectedSchema.label}` : ""}
                disabled
                className="input-readonly"
              />
            </div>
            {/* 5. Form con valori */}
            {selectedSchema ? (
              <div className="input-group">
                <JsonSchemaForm
                  schema={selectedSchema.schema}
                  formData={{
                    ...formData,
                    TipoParte: Number(selectedSchemaId),
                    TipoParteDescripcion: selectedSchema.label || "",
                  }}
                  onChange={(data) => setFormData(data.formData)}
                />
              </div>
            ) : (
              <div className="input-group">
                <p style={{ color: "#666" }}>
                  No se pudo cargar el formulario dinámico para este parte. Solo
                  se actualizará la asignación.
                </p>
              </div>
            )}

            {!effectiveClientCode && (
              <div className="input-group">
                <p style={{ color: "#d9822b" }}>
                  No se pudo determinar el cliente del parte. Se intentará usar
                  el cliente seleccionado actualmente.
                </p>
              </div>
            )}
          </div>

          {/* BOTTONI FISSI IN BASSO (FUORI dal div scrollabile!) */}
          <div className="modal-edit-actions">
            <span className="boton_opciones" onClick={saving ? undefined : handleSave}>
                  Aceptar
                </span>
                <span className="boton_cancelar" onClick={onClose}>
                  Cancelar
                </span>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ModalEditAssignment;